import { A } from "@solidjs/router";
import { useQuery } from "@tanstack/solid-query";
import { Ban, CrownIcon, Plus, RefreshCcw, Settings } from "lucide-solid";
import {
	type Accessor,
	type Component,
	createMemo,
	For,
	type JSXElement,
	Match,
	Show,
	Switch
} from "solid-js";
import { type ITaskLimit, TaskApi } from "~/js/network/TaskApi";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import {
	Button,
	calculatePercentage,
	getIsUserInfinity,
	getIsUserOnLegacyPlan,
	getNextBillingLegacyText,
	getNextBillingText,
	getTariffTransitionDeltailedText,
	getTariffTransitionText,
	getUserCreditsBalance,
	getUserCreditsTotal,
	getUserPlanName,
	isUserCanUseCredits,
	isUserFree,
	isUserFromAppSumo,
	isUserFrozen
} from "~/solidJs/shared";
import { AirtableProxiApi } from "~/solidJs/shared/api/AirtableProxyApi";
import type { IUser } from "~/solidJs/shared/helpers/types";
import {
	createBuyCreditsProps,
	createPricingProps,
	openPricingPage
} from "~/solidJs/shared/models/pageParams/pricingPageParams";
import { openPricingUnsubscribePage } from "~/solidJs/shared/models/pageParams/unsubscribePagePrams";

import {
	CreditsSection,
	PlanStatus,
	StatusBadge,
	SubscriptionInfoEntity
} from "./SubscriptionWidget";

// Local types for user subscription data with Accessors
interface UserSubscriptionData {
	planName: Accessor<string | undefined>;
	planStatus: Accessor<PlanStatus>;
	creditsLeft: Accessor<number | undefined>;
	totalCredits: Accessor<number | string | undefined>;
	progressPercentage: Accessor<number | undefined>;
	refreshText: Accessor<string | undefined>;
	infoMessage: Accessor<string | undefined>;

	// User status flags
	isUserInfinity: Accessor<boolean | undefined>;
	isUserOnLegacyPlan: Accessor<boolean | undefined>;
	isUserFromAppSumo: Accessor<boolean | undefined>;
	isUserFree: Accessor<boolean | undefined>;
	isUserFrozen: Accessor<boolean>;
	isUserCanUseCredits: Accessor<boolean | undefined>;
	tariffTransitionText: Accessor<string | null | undefined>;
	tariffTransitionDetailedText: Accessor<string | null | undefined>;
	shouldRenderNodesLimits: Accessor<boolean | undefined>;
}

// Расширенный интерфейс для лимитов с именами
interface ITaskLimitWithName extends ITaskLimit {
	displayName: string;
}

const TaskLimitItem: Component<{ taskLimit: ITaskLimitWithName }> = (props) => {
	const remainingTasks = () =>
		(props.taskLimit.limits_per_month || 0) - props.taskLimit.count_tasks;
	const progressPercentage = () => {
		if (props.taskLimit.limits_per_month === 0) return 0;
		const limit = props.taskLimit.limits_per_month || 0;
		return ((limit - props.taskLimit.count_tasks) / (limit || 0)) * 100;
	};

	const getProgressColor = () => {
		const percentage = progressPercentage();
		if (percentage <= 20) return "bg-status-accent-error";
		if (percentage <= 30) return "bg-background-orange";
		return "bg-[#999999]";
	};

	return (
		<div class="flex flex-col gap-2">
			{/* Name + Credits */}
			<div class="flex w-full items-center justify-between">
				{/* Name */}
				<div class="flex items-center gap-1">
					<span class="caption-12-medium text-text-primary">
						{props.taskLimit.displayName}
					</span>
				</div>

				{/* Credits */}
				<div class="flex items-center gap-1">
					<span class="typography-text-12-medium text-text-primary">
						{remainingTasks()}
					</span>
					<span class="typography-text-12-medium text-text-secondary">/</span>
					<span class="typography-text-12-medium text-text-secondary">
						{props.taskLimit.limits_per_month || 0}
					</span>
				</div>
			</div>

			{/* Progress bar */}
			<div class="relative h-2 w-full overflow-hidden rounded-full bg-background-gray-medium">
				<div
					class={`h-full rounded-full transition-all duration-300 ${getProgressColor()}`}
					style={{
						width: `${Math.min(progressPercentage(), 100)}%`
					}}
				/>
			</div>
		</div>
	);
};

export const TaskLimitsSection: Component<{
	UpgradeFeatures?: JSXElement;
	shouldRenderNodesLimits?: boolean;
	refreshText?: JSXElement | string;
}> = (props) => {
	const taskLimitsQuery = useQuery(() => TaskApi.getTaskLimitsOption());

	const nodeNamesQuery = useQuery(() => ({
		queryKey: ["mlBaseNodeNames"],
		queryFn: () => AirtableProxiApi.fetchMlBaseNodeName()
	}));

	const mergedTaskLimits = createMemo(() => {
		const taskLimits = taskLimitsQuery.data;
		const nodeNames = nodeNamesQuery.data;

		if (!taskLimits || !nodeNames) return [];

		const nameMap = new Map<string, string>(
			nodeNames.map((record) => [record.fields["Global ID"], record.fields.Name])
		);

		return taskLimits.map((taskLimit) => ({
			...taskLimit,
			displayName: nameMap.get(taskLimit.id_global) || taskLimit.id_global
		})) as ITaskLimitWithName[];
	});

	const isLoading = () => taskLimitsQuery.isLoading || nodeNamesQuery.isLoading;
	const hasData = () => Boolean(mergedTaskLimits().length > 0);

	return (
		<>
			<Show when={props.refreshText} keyed>
				{(text) => <div class="caption-12-medium text-text-secondary">{text}</div>}
			</Show>
			<Show when={props.shouldRenderNodesLimits}>
				<div class="flex flex-col gap-6">
					{/* Header */}
					<div class="flex w-full items-end justify-between gap-6">
						<div class="header-18-semibold text-text-primary">AI Tools usage</div>
						<div class="caption-12-semibold text-text-tertiary uppercase">
							left / total
						</div>
					</div>

					<Show
						when={hasData() && !isLoading()}
						fallback={
							<div class="flex items-center justify-center py-8">
								<div class="typography-text-14-medium text-text-secondary">
									{isLoading() ? "Loading..." : "No data"}
								</div>
							</div>
						}
					>
						<For each={mergedTaskLimits()}>
							{(taskLimit) => <TaskLimitItem taskLimit={taskLimit} />}
						</For>
					</Show>
				</div>
			</Show>
			{props.UpgradeFeatures}
		</>
	);
};

export const ManageUserSubscriptionAndCreditsFeature: Component<{
	isUserFromAppSumo?: boolean;
	isUserFree?: boolean;
}> = (props) => {
	const user = createMemo(() => getUserStore());
	const isCanceled = () => !!user()?.cancel_at;

	return (
		<div class="flex gap-1">
			<Switch>
				<Match when={props.isUserFromAppSumo}>
					<Button
						component="a"
						href="https://appsumo.com/account/products"
						target="_blank"
						variant="TertiaryOverlay"
						disabled={isCanceled()}
					>
						<Ban class="h-4 w-4" />
						<span class="caption-12-medium">Cancel plan</span>
					</Button>
					<Button
						component="a"
						href="https://appsumo.com/account/products"
						target="_blank"
						variant="TertiaryOverlay"
					>
						<RefreshCcw class="h-4 w-4" />
						<span class="caption-12-medium">Change plan</span>
					</Button>
				</Match>
				<Match when={props.isUserFree}>
					<Button
						variant="TertiaryOverlay"
						onClick={() => {
							EventAnalytics.subscriptionUiInteracted("manage_subscription");
							openPricingPage({
								source: "namage-subscription-free"
							});
						}}
					>
						<Settings class="h-4 w-4" />
						<span class="caption-12-medium">Manage subscription</span>
					</Button>
				</Match>
				<Match when={!props.isUserFree}>
					<Button
						variant="TertiaryOverlay"
						onClick={() => {
							openPricingUnsubscribePage();
							EventAnalytics.subscriptionUiInteracted("cancel_plan");
						}}
						disabled={isCanceled()}
					>
						<Ban class="h-4 w-4" />
						<span class="caption-12-medium">Cancel plan</span>
					</Button>
					<Button
						variant="TertiaryOverlay"
						onClick={() => {
							EventAnalytics.subscriptionUiInteracted("change_plan");
							openPricingPage({
								source: "account-cancel-plan"
							});
						}}
						disabled={isUserFrozen(user())}
					>
						<RefreshCcw class="h-4 w-4" />
						<span class="caption-12-medium">Change plan</span>
					</Button>
				</Match>
			</Switch>
		</div>
	);
};

interface UpgradeSubscriptionUserFeatureProps {
	shouldShowUpgradeButton?: boolean;
	shouldShowMoreCreditsButton?: boolean;
	source?: string;
	disabled?: boolean;
}

export const UpgradeSubscriptionUserFeature: Component<
	UpgradeSubscriptionUserFeatureProps & {
		isUserFromAppSumo?: boolean;
	}
> = (props) => {
	return (
		<div class="flex items-center gap-2">
			<Show when={props.shouldShowMoreCreditsButton}>
				<Button
					component={A}
					{...createBuyCreditsProps(props.source || "get_more_credits")}
					variant="SecondaryOverlay"
					size="large"
					disabled={props.disabled}
				>
					<Plus size={16} class="text-interaction-violet" />
					<span class="typography-text-14-medium text-interaction-violet">
						Get more credits
					</span>
				</Button>
			</Show>

			<Show when={true || props.shouldShowUpgradeButton}>
				<Switch
					fallback={
						<Button
							component={"a"}
							target="_blank"
							variant="actionviolent"
							size="large"
							class="gap-2.5"
							disabled={props.disabled}
							{...createPricingProps("account")}
						>
							<CrownIcon class="h-4 w-4 text-white" />
							<span class="typography-text-14-medium text-white">Upgrade</span>
						</Button>
					}
				>
					<Match when={props.isUserFromAppSumo}>
						<Button
							component={"a"}
							target="_blank"
							variant="actionviolent"
							size="large"
							class="gap-2.5"
							disabled={props.disabled}
							href="https://appsumo.com/account/products "
						>
							<CrownIcon class="h-4 w-4 text-white" />
							<span class="typography-text-14-medium text-white">Upgrade</span>
						</Button>
					</Match>
				</Switch>
			</Show>
		</div>
	);
};

const getPlanStatusAccordUserStatus = (user: IUser | null): PlanStatus => {
	if (!user) return PlanStatus.Inactive;
	switch (user.subscription_status) {
		case "active":
			return PlanStatus.Active;
		case "inactive":
			return PlanStatus.Inactive;
		case "frozen":
			return PlanStatus.Frozen;
		case "due":
			return PlanStatus.Due;
		default:
			return PlanStatus.Inactive;
	}
};

export const prepeareUserPropsSubscriptionEntity = (): UserSubscriptionData => {
	const userStore = createMemo(() => getUserStore());

	return {
		planName: () => getUserPlanName(userStore()),
		planStatus: () => getPlanStatusAccordUserStatus(userStore()),
		creditsLeft: () => getUserCreditsBalance(userStore()),
		totalCredits: () => getUserCreditsTotal(userStore()),
		progressPercentage: () =>
			calculatePercentage(
				0,
				getUserCreditsTotal(userStore()),
				getUserCreditsBalance(userStore())
			),
		refreshText: () =>
			getTariffTransitionDeltailedText(userStore()) ||
			getNextBillingLegacyText(userStore()) ||
			getNextBillingText(userStore()),
		infoMessage: () =>
			isUserFrozen(userStore())
				? "Your subscription and credits are temporarily frozen. You can use credits from your team account"
				: undefined,
		isUserInfinity: () => getIsUserInfinity(userStore()),
		isUserOnLegacyPlan: () => getIsUserOnLegacyPlan(userStore()),
		isUserFromAppSumo: () => isUserFromAppSumo(userStore()),
		isUserFree: () => isUserFree(userStore()),
		isUserFrozen: () => isUserFrozen(userStore()),
		isUserCanUseCredits: () => isUserCanUseCredits(userStore()),
		tariffTransitionText: () => getTariffTransitionText(userStore()),
		tariffTransitionDetailedText: () => getTariffTransitionDeltailedText(userStore()),
		shouldRenderNodesLimits: () => undefined
	};
};

export const SubscriptionInfoWidget: Component = () => {
	const props = prepeareUserPropsSubscriptionEntity();

	const UpgradeFeatures = (
		<UpgradeSubscriptionUserFeature
			source="personal_overview"
			shouldShowMoreCreditsButton={props.isUserCanUseCredits()}
			shouldShowUpgradeButton
			isUserFromAppSumo={props.isUserFromAppSumo()}
			disabled={props.isUserFrozen()}
		/>
	);

	const Badges = (
		<>
			<StatusBadge status={props.planStatus()}>{props.planStatus() || "Active"}</StatusBadge>
			<Show when={props.isUserOnLegacyPlan() && !props.isUserFromAppSumo()}>
				<StatusBadge status={PlanStatus.Legacy} />
			</Show>
			<Show when={props.tariffTransitionText()} keyed>
				{(text) => <StatusBadge status={PlanStatus.NewTariff}>{text}</StatusBadge>}
			</Show>
		</>
	);

	const Content = (
		<Switch>
			<Match when={props.isUserOnLegacyPlan()}>
				<TaskLimitsSection
					shouldRenderNodesLimits={props.shouldRenderNodesLimits()}
					UpgradeFeatures={UpgradeFeatures}
					refreshText={props.refreshText()}
				/>
			</Match>
			<Match when={!props.isUserOnLegacyPlan()}>
				<CreditsSection
					creditsLeft={props.creditsLeft()}
					totalCredits={props.totalCredits()}
					progressPercentage={props.progressPercentage()}
					refreshText={props.refreshText()}
					UpgradeFeatures={UpgradeFeatures}
					isUserInfinity={props.isUserInfinity()}
				/>
			</Match>
		</Switch>
	);

	return (
		<SubscriptionInfoEntity
			planName={props.planName()}
			badges={Badges}
			manageSubscriptionAction={
				<ManageUserSubscriptionAndCreditsFeature
					isUserFromAppSumo={props.isUserFromAppSumo()}
					isUserFree={props.isUserFree()}
				/>
			}
			content={Content}
			infoMessage={props.infoMessage()}
		/>
	);
};
